import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Typography } from '@mui/material';

import BackToInquiriesButton from '@components/BackToInquiriesButton/BackToInquiriesButton';
import EmptyCard from '@components/EmptyCard/EmptyCard';
import InquiryStatus from '@components/InquiryStatus/InquiryStatus';
import Loadable from '@components/Loadable/Loadable';
import PrintButton from '@components/PrintButton/PrintButton';
import useToggle from '@hooks/useToggle';
import { TInquiryStatus } from '@interfaces/inquiries';
import { TRiskLevel } from '@interfaces/riskLevels';
import { IUserEnteredSupplementAddNote, TSupplementResponseStatus } from '@interfaces/supplements';
import urls from '@routes/urls';
import { getInquiryResponseUrl } from '@routes/utils';
import { handleRemoveSupplementFromInquiryDraft } from '@store/features/inquiryDraft/removeSupplementFromInquiryDraft/actions';
import { removeSupplementFromInquiryDraftIsLoadingSelector } from '@store/features/inquiryDraft/removeSupplementFromInquiryDraft/selectors';
import {
  handleGetInquiryResponse,
  handleGetSupplementResponseDetails,
  handleMarkSupplementResponseAsViewed,
  handleSubmitSupplementNeedsInfoForm,
} from '@store/features/inquiryResponse/actions';
import {
  inquiryResponseActiveSupplementDataSelector,
  inquiryResponseActiveSupplementErrorSelector,
  inquiryResponseActiveSupplementIdSelector,
  inquiryResponseActiveSupplementIsLoadingSelector,
  inquiryResponseDataSelector,
  inquiryResponseErrorSelector,
  inquiryResponseIsLoadingSelector,
  inquiryResponseShowRiskLevelDetailsSelector,
} from '@store/features/inquiryResponse/selectors';
import {
  removeActiveSupplement,
  removeInquiryResponseDetails,
  setShowRiskLevelDetails,
} from '@store/features/inquiryResponse/slice';
import {
  getHasUnseenResponses,
  getIsLastOneThatNeedsInfo,
} from '@store/features/inquiryStatuses/utils';
import useDispatch from '@store/hooks/useDispatch';
import NotificationModal from '@views/InquiryDraftPage/NotificationModal/NotificationModal';
import {
  INotificationModalData,
  TSupplementType,
} from '@views/InquiryDraftPage/NotificationModal/NotificationModal.interface';
import { EventType, useEventBus } from '@utils/eventBus';
import formatDate from '@utils/formatDate';
import handleToast from '@utils/handleToast';

import RiskLevelModal from './RiskLevelModal/RiskLevelModal';
import SupplementResponseCard from './SupplementResponseCard/SupplementResponseCard';
import SupplementResponseDetails from './SupplementResponseDetails/SupplementResponseDetails';
import { shouldShowPrintButton } from './utils';

import './style.scss';

const InquiryResponsePage: FC = () => {
  const { t } = useTranslation();
  const eventBus = useEventBus('dietarySupplementResponsePage');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { inquiryId } = useParams();
  const [isNotificationModalOpen, onNotificationModalOpen, onNotificationModalClose] = useToggle();
  const [dietarySupplementInModal, setDietarySupplementInModal] =
    useState<INotificationModalData | null>(null);

  const data = useSelector(inquiryResponseDataSelector);
  const isLoading = useSelector(inquiryResponseIsLoadingSelector);
  const error = useSelector(inquiryResponseErrorSelector);
  const activeSupplementId = useSelector(inquiryResponseActiveSupplementIdSelector);
  const activeSupplement = useSelector(inquiryResponseActiveSupplementDataSelector);
  const activeSupplementIsLoading = useSelector(inquiryResponseActiveSupplementIsLoadingSelector);
  const activeSupplementError = useSelector(inquiryResponseActiveSupplementErrorSelector);
  const deleteNeedsInfoItemIsInProgress = useSelector(
    removeSupplementFromInquiryDraftIsLoadingSelector,
  );
  const hasUnseenResponses = useMemo(() => (data ? getHasUnseenResponses(data) : false), [data]);
  const isLastOneThatNeedsInfo = useMemo(
    () => (data ? getIsLastOneThatNeedsInfo(data) : false),
    [data],
  );

  const isOneItemQuery = useMemo(() => data?.supplementInquiriesDto?.length === 1, [data]);

  const showLoader = deleteNeedsInfoItemIsInProgress || isLoading;

  const showRiskLevelDetails = useSelector(inquiryResponseShowRiskLevelDetailsSelector);
  const setShowRiskLevelModal = useCallback(
    (riskLevel: TRiskLevel) => {
      dispatch(setShowRiskLevelDetails(riskLevel));
    },
    [dispatch],
  );
  const handleCloseRiskLevelModal = useCallback(() => {
    dispatch(setShowRiskLevelDetails(null));
  }, [dispatch]);

  const handleFetchInquiryResponse = useCallback(() => {
    inquiryId && dispatch(handleGetInquiryResponse(+inquiryId));
  }, [dispatch, inquiryId]);

  const handleConfirmRemovalFromInquiry = useCallback(
    (data: INotificationModalData) => {
      if (!data) {
        return;
      }
      dispatch(handleRemoveSupplementFromInquiryDraft({ id: Number(data.itemId) }));
    },
    [dispatch],
  );

  useEffect(handleFetchInquiryResponse, [handleFetchInquiryResponse]);

  useEffect(() => {
    if (activeSupplement?.supplementInquiryStatusId === TSupplementResponseStatus.New) {
      dispatch(handleMarkSupplementResponseAsViewed(activeSupplement.id));
    }
  }, [dispatch, activeSupplement]);

  const handleOpenSupplementDetails = useCallback(
    (supplementId: number) => {
      dispatch(handleGetSupplementResponseDetails(supplementId));
    },
    [dispatch],
  );

  const handleRetryOpenSupplementDetails = useCallback(() => {
    activeSupplementId && handleOpenSupplementDetails(activeSupplementId);
  }, [handleOpenSupplementDetails, activeSupplementId]);

  const handleCloseSupplementDetails = useCallback(() => {
    dispatch(removeActiveSupplement());
  }, [dispatch]);

  const handleSubmitNeedsInfoForm = useCallback(
    (values: IUserEnteredSupplementAddNote) =>
      dispatch(
        handleSubmitSupplementNeedsInfoForm({
          userEnteredSupplementId: activeSupplement?.userEnteredSupplementId ?? 0,
          ...values,
        }),
      ),
    [dispatch, activeSupplement],
  );

  const handleOpenModalConfirmation = useCallback(
    (supplementId: number): void => {
      onNotificationModalOpen();
      setDietarySupplementInModal({ itemId: supplementId, type: TSupplementType.INQUIRY_ITEM });
    },
    [onNotificationModalOpen],
  );

  const handleRemoveSupplementFromInquirySuccess = useCallback(() => {
    setDietarySupplementInModal(null);
    isOneItemQuery ? navigate(urls.inquiries) : handleFetchInquiryResponse();
  }, [handleFetchInquiryResponse, isOneItemQuery, navigate]);

  useEffect(() => {
    eventBus.on(EventType.NEEDS_INFO_FORM_SUBMITTED, () => {
      handleToast('success', t('inquiryResponse.needsInfoFormSubmitSuccess'));
      handleCloseSupplementDetails();
      isLastOneThatNeedsInfo ? navigate(urls.inquiries) : handleFetchInquiryResponse();
    });
    eventBus.on(
      EventType.REMOVE_SUPPLEMENT_FROM_INQUIRY_DRAFT_SUCCESS,
      handleRemoveSupplementFromInquirySuccess,
    );
    return () =>
      eventBus.off([
        EventType.NEEDS_INFO_FORM_SUBMITTED,
        EventType.REMOVE_SUPPLEMENT_FROM_INQUIRY_DRAFT_SUCCESS,
      ]);
  }, [
    eventBus,
    navigate,
    handleCloseSupplementDetails,
    handleFetchInquiryResponse,
    isLastOneThatNeedsInfo,
    t,
  ]);

  useEffect(
    () => () => {
      dispatch(removeInquiryResponseDetails());
      dispatch(removeActiveSupplement());
    },
    [dispatch],
  );

  return (
    <Container maxWidth="lg" className="page inquiry-response-page">
      <BackToInquiriesButton />

      <Typography variant="h1" className="inquiry-response-page__title">
        {t('inquiryResponse.title')}
      </Typography>

      <div className="inquiry-response-page__content">
        <div className="inquiry-response-page__main">
          <div className="inquiry-response-page__header">
            <Typography variant="h2" className="inquiry-response-page__supplements-title">
              {t('inquiryResponse.supplementsTitle')}
            </Typography>

            {data && shouldShowPrintButton(data) && (
              <PrintButton
                buttonText={t('common.printResponses')}
                linkToPrintPage={`${getInquiryResponseUrl(+inquiryId!)}/print`}
              />
            )}
          </div>

          <Loadable
            isLoading={showLoader}
            error={error && t('errors.inquiryResponseError')}
            onRetry={handleFetchInquiryResponse}
          >
            {data && (
              <>
                <div className="inquiry-response-page__inquiry">
                  <div className="inquiry-response-page__inquiry-meta">
                    <div className="inquiry-response-page__inquiry-name">{data.name}</div>
                    <div className="inquiry-response-page__inquiry-date">
                      {t('inquiryResponse.submitted')} {formatDate(data.submittedOn)}
                    </div>
                  </div>

                  {data.inquiryStatusId !== TInquiryStatus.Complete && (
                    <InquiryStatus value={data.inquiryStatusId} />
                  )}
                </div>

                <div className="inquiry-response-page__supplements-list">
                  {data.supplementInquiriesDto.map(item => (
                    <SupplementResponseCard
                      className="inquiry-response-page__supplement-item"
                      id={item.supplementInquiryId}
                      name={item.name}
                      status={item.supplementInquiryStatusId}
                      riskLevelId={item.riskLevelId}
                      isActive={item.supplementInquiryId === activeSupplementId}
                      isDeactivated={!item.isActive}
                      onRemove={handleOpenModalConfirmation}
                      onShowDetails={handleOpenSupplementDetails}
                      setShowRiskLevelModal={setShowRiskLevelModal}
                      key={item.supplementInquiryId}
                    />
                  ))}
                </div>
              </>
            )}
          </Loadable>
        </div>

        {activeSupplementId ? (
          <SupplementResponseDetails
            className="inquiry-response-page__sidebar"
            data={activeSupplement}
            isLoading={activeSupplementIsLoading}
            error={activeSupplementError}
            hasUnseenResponses={hasUnseenResponses}
            isLastOneThatNeedsInfo={isLastOneThatNeedsInfo}
            onShowRiskLevelModal={setShowRiskLevelModal}
            onSubmitNeedsInfoForm={handleSubmitNeedsInfoForm}
            onClose={handleCloseSupplementDetails}
            onRetry={handleRetryOpenSupplementDetails}
          />
        ) : (
          <EmptyCard
            className="inquiry-response-page__sidebar"
            icon="/images/img-dietarysupplement.svg"
            description={t('inquiryResponse.supplementDetailsPlaceholder')}
          />
        )}
      </div>

      <RiskLevelModal
        open={showRiskLevelDetails !== null}
        onClose={handleCloseRiskLevelModal}
        defaultRiskLevel={showRiskLevelDetails}
      />

      <NotificationModal
        dataInModal={dietarySupplementInModal}
        onClose={onNotificationModalClose}
        onConfirm={handleConfirmRemovalFromInquiry}
        open={isNotificationModalOpen}
      />
    </Container>
  );
};

export default InquiryResponsePage;
